"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TestClient = exports.ModelType = void 0;
const axios_1 = __importDefault(require("axios"));
var ModelType;
(function (ModelType) {
    ModelType["Address"] = "address";
    ModelType["Category"] = "category";
    ModelType["Communication"] = "communication";
    ModelType["Event"] = "event";
    ModelType["Location"] = "location";
    ModelType["Organization"] = "organization";
    ModelType["PaymentMethod"] = "payment_method";
    ModelType["Person"] = "person";
    ModelType["SubscriptionProduct"] = "subscription_product";
    ModelType["Ticket"] = "ticket";
    ModelType["TicketCategory"] = "ticket_category";
    ModelType["TicketItem"] = "ticket_item";
    ModelType["TicketPurchase"] = "ticket_purchase";
    ModelType["TicketRefund"] = "ticket_refund";
    ModelType["TicketTier"] = "ticket_tier";
    ModelType["TicketType"] = "ticket_type";
    ModelType["Transaction"] = "transaction";
    ModelType["User"] = "user";
})(ModelType || (exports.ModelType = ModelType = {}));
class TestClient {
    constructor(config = {}) {
        this.createdEntities = new Map();
        this.client = axios_1.default.create({
            baseURL: config.baseUrl || process.env.APP_URL || 'https://duesy.test',
            responseType: 'json',
        });
    }
    getEndpoint(type) {
        switch (type) {
            case ModelType.Address:
                return '/testing/models/addresses';
            case ModelType.Category:
                return '/testing/models/categories';
            case ModelType.Communication:
                return '/testing/models/communications';
            case ModelType.Event:
                return '/testing/models/events';
            case ModelType.Location:
                return '/testing/models/locations';
            case ModelType.Organization:
                return '/testing/models/organizations';
            case ModelType.PaymentMethod:
                return '/testing/models/paymentMethods';
            case ModelType.Person:
                return '/testing/models/persons';
            case ModelType.SubscriptionProduct:
                return '/testing/models/subscription-products';
            case ModelType.TicketCategory:
                return '/testing/models/ticketCategories';
            case ModelType.Ticket:
                return '/testing/models/tickets';
            case ModelType.TicketItem:
                return '/testing/models/ticketItems';
            case ModelType.TicketPurchase:
                return '/testing/models/ticketPurchases';
            case ModelType.TicketRefund:
                return '/testing/models/ticket-refunds';
            case ModelType.TicketTier:
                return '/testing/models/ticketTiers';
            case ModelType.TicketType:
                return '/testing/models/ticketTypes';
            case ModelType.Transaction:
                return '/testing/models/transactions';
            case ModelType.User:
                return '/testing/models/users';
        }
    }
    async store(endpoint, payload) {
        const response = await this.client.post(endpoint, payload);
        return (await response.data);
    }
    track(type, entity) {
        if (!this.createdEntities.has(type)) {
            this.createdEntities.set(type, []);
        }
        this.createdEntities.get(type).push(entity);
    }
    async cleanup() {
        const deletionOrder = [
            ModelType.TicketPurchase,
            ModelType.Transaction,
            ModelType.TicketRefund,
            ModelType.TicketItem,
            ModelType.Ticket,
            ModelType.TicketType,
            ModelType.TicketTier,
            ModelType.TicketCategory,
            ModelType.Event,
            ModelType.Location,
            ModelType.Category,
            ModelType.PaymentMethod,
            ModelType.Person,
            ModelType.Address,
            ModelType.SubscriptionProduct,
            ModelType.Communication,
            ModelType.Organization,
            ModelType.User,
        ];
        for (const type of deletionOrder) {
            const entities = [...(this.createdEntities.get(type) || [])];
            for (const entity of entities) {
                const destroyed = await this.destroy(type, entity.id);
                if (!destroyed) {
                    return false;
                }
            }
        }
        this.createdEntities.clear();
        return true;
    }
    async show(type, id) {
        const response = await this.client.get(`${this.getEndpoint(type)}/${id}`);
        return (await response.data);
    }
    /**
     * Clears this caller's buckets on the named rate limiters (e.g.
     * `registration-submit`). E2E workers share one IP, so per-IP limits on public
     * forms would otherwise throttle a fast run.
     */
    async clearRateLimits(limiters) {
        await this.client.delete('/testing/rate-limits', { data: { limiters } });
    }
    async variable(key) {
        const response = await this.client.get(`/testing/variables/${key}`);
        return (await response.data);
    }
    async destroy(type, id) {
        const response = await this.client.delete(`${this.getEndpoint(type)}/${id}`);
        const success = response.status === 200;
        if (success) {
            const entities = this.createdEntities.get(type);
            if (entities) {
                const index = entities.findIndex((e) => e.id === id);
                if (index !== -1) {
                    entities.splice(index, 1);
                }
            }
        }
        return success;
    }
    async createOrganization(attributes = {}, extras = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.Organization), {
            attributes: {
                ...attributes,
            },
            extras: extras,
        });
        this.track(ModelType.Organization, entity);
        return entity;
    }
    async createEvent(organization, attributes = {}, extras = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.Event), {
            attributes: {
                organization_id: organization.id,
                ...attributes,
            },
            extras: extras,
        });
        this.track(ModelType.Event, entity);
        return entity;
    }
    async createLocation(organization, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.Location), {
            attributes: {
                organization_id: organization.id,
                ...attributes,
            },
        });
        this.track(ModelType.Location, entity);
        return entity;
    }
    async createAddress(organization, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.Address), {
            attributes: {
                organization_id: organization.id,
                ...attributes,
            },
        });
        this.track(ModelType.Address, entity);
        return entity;
    }
    async createPerson(organization, attributes = {}, extras = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.Person), {
            attributes: {
                organization_id: organization.id,
                ...attributes,
            },
            extras: extras,
        });
        this.track(ModelType.Person, entity);
        return entity;
    }
    /**
     * `extras.ages` / `extras.classifications` are ordered name lists seeded as the
     * category's ladders; the response carries both, lowest position first.
     */
    async createCategory(organization, attributes = {}, extras = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.Category), {
            attributes: {
                organization_id: organization.id,
                ...attributes,
            },
            extras: extras,
        });
        this.track(ModelType.Category, entity);
        return entity;
    }
    /**
     * A registration form (live unless `attributes.status` says otherwise) with
     * `extras.fields` published as its field set and `extras.field_descriptions`
     * on its built-in fields. Not tracked: it is removed with its organization.
     */
    async createRegistrationForm(organization, attributes = {}, extras = {}) {
        return this.store('/testing/models/registrationForms', {
            attributes: {
                organization_id: organization.id,
                ...attributes,
            },
            extras: extras,
        });
    }
    /**
     * Saves the event's registration settings (enabled unless `attributes.enabled`
     * is false) and offers `extras.ages`, as the Setup tab's "Save registration
     * settings" does. Not tracked: it is removed with its event.
     */
    async createEventRegistration(event, attributes = {}, extras = {}) {
        return this.store('/testing/models/eventRegistrations', {
            attributes: {
                event_id: event.id,
                ...attributes,
            },
            extras: extras,
        });
    }
    async createTicketCategory(organization, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.TicketCategory), {
            attributes: {
                organization_id: organization.id,
                ...attributes,
            },
        });
        this.track(ModelType.TicketCategory, entity);
        return entity;
    }
    async createTicketTier(organization, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.TicketTier), {
            attributes: {
                organization_id: organization.id,
                ...attributes,
            },
        });
        this.track(ModelType.TicketTier, entity);
        return entity;
    }
    async createTicketType(event, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.TicketType), {
            attributes: {
                event_id: event.id,
                ...attributes,
            },
        });
        this.track(ModelType.TicketType, entity);
        return entity;
    }
    async createTicket(event, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.Ticket), {
            attributes: {
                event_id: event.id,
                ...attributes,
            },
        });
        this.track(ModelType.Ticket, entity);
        return entity;
    }
    async createTicketItem(ticket, type, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.TicketItem), {
            attributes: {
                ticket_id: ticket.id,
                ...(type && { ticket_type_id: type.id }),
                ...attributes,
            },
        });
        this.track(ModelType.TicketItem, entity);
        return entity;
    }
    async createTicketRefund(ticket, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.TicketRefund), {
            attributes: {
                ticket_id: ticket.id,
                ...attributes,
            },
        });
        this.track(ModelType.TicketRefund, entity);
        return entity;
    }
    async createTicketPurchase(event, ticketType, attrs = {}) {
        const { is_apple_pay, is_tap_to_pay, purchase_source, base } = attrs;
        const person = await this.createPerson({ id: event.organization_id });
        const paymentMethod = await this.createPaymentMethod(person, {
            ...(is_apple_pay !== undefined && { is_apple_pay }),
        });
        const entity = await this.store(this.getEndpoint(ModelType.Ticket), {
            attributes: {
                event_id: event.id,
                payment_method_id: paymentMethod.id,
                person_id: person.id,
                ...(ticketType && { ticket_type_id: ticketType.id }),
                ...(purchase_source !== undefined && { purchase_source }),
                ...(base !== undefined && { base }),
            },
        });
        this.track(ModelType.Ticket, entity);
        if (is_tap_to_pay !== undefined) {
            await this.createTransaction(entity, { is_tap_to_pay });
        }
        return entity;
    }
    async createTransaction(ticket, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.Transaction), {
            attributes: {
                ticket_id: ticket.id,
                ...attributes,
            },
        });
        this.track(ModelType.Transaction, entity);
        return entity;
    }
    async createSubscriptionProduct(organization, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.SubscriptionProduct), {
            attributes: {
                organization_id: organization.id,
                ...attributes,
            },
        });
        this.track(ModelType.SubscriptionProduct, entity);
        return entity;
    }
    async createPaymentMethod(person, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.PaymentMethod), {
            attributes: {
                person_id: person.id,
                ...attributes,
            },
        });
        this.track(ModelType.PaymentMethod, entity);
        return entity;
    }
    async createUser(attributes = {}, extras = {}) {
        const response = await this.store(this.getEndpoint(ModelType.User), {
            attributes: {
                ...attributes,
            },
            extras: extras,
        });
        this.track(ModelType.User, response.user);
        return response;
    }
    async createCommunication(organization, targets, attributes = {}) {
        const entity = await this.store(this.getEndpoint(ModelType.Communication), {
            attributes: {
                organization_id: organization.id,
                targets,
                ...attributes,
            },
        });
        this.track(ModelType.Communication, entity);
        return entity;
    }
}
exports.TestClient = TestClient;
